import type { TFunction } from 'i18next'
import { errorText } from 'utils/errorText'
import { reportClientError } from 'utils/reportClientError'

/**
 * Maps known marketplace error signatures (Solidity custom errors and
 * a few well-known generic ones) to friendly i18n keys. Used by the
 * marketplace UI so toast messages explain what happened in plain
 * language instead of dumping a hex revert.
 */

interface DecoderEntry {
  /** Substring that must appear in the raw ethers error. */
  match: string
  /** i18n key under `marketplace.errors.*`. */
  key: string
}

const ENTRIES: DecoderEntry[] = [
  // Marketplace custom errors (matched by name in the revert message).
  { match: 'SellerNoLongerOwnsToken', key: 'sellerNoLongerOwnsToken' },
  { match: 'MissingNftApproval', key: 'missingNftApproval' },
  { match: 'CollectionNotAllowed', key: 'collectionNotAllowed' },
  { match: 'ListingNotActive', key: 'listingNotActive' },
  { match: 'ListingAlreadyExists', key: 'listingAlreadyExists' },
  { match: 'PriceMustBePositive', key: 'priceMustBePositive' },
  { match: 'NotTokenOwner', key: 'notTokenOwner' },
  { match: 'NotSeller', key: 'notSeller' },
  { match: 'PriceAboveMax', key: 'priceAboveMax' },
  { match: 'ListingStillValid', key: 'listingStillValid' },
  // OpenZeppelin. The marketplace is on OZ 5, which raises custom errors;
  // MPRO and the licence collections are older and still revert with
  // strings, so both forms have to be recognised.
  { match: 'EnforcedPause', key: 'paused' },
  { match: 'OwnableUnauthorizedAccount', key: 'notOwner' },
  { match: 'Pausable: paused', key: 'paused' },
  { match: 'Ownable: caller is not the owner', key: 'notOwner' },
  // ERC-20 / ERC-721 fallbacks.
  { match: 'ERC20: insufficient allowance', key: 'mproAllowanceInsufficient' },
  { match: 'transfer amount exceeds balance', key: 'mproBalanceInsufficient' },
  { match: 'caller is not token owner', key: 'nftCallerNotOwner' },
  { match: 'transfer caller is not owner nor approved', key: 'missingNftApproval' },
  // No banner announces this any more — the collections are open. If one were
  // ever closed again, the refusal still has to read as a sentence.
  { match: 'transfer not enabled', key: 'transfersNotEnabled' },
  { match: 'transfers disabled', key: 'transfersNotEnabled' },
  // Wallet / RPC layer.
  // "unknown account" means the request to sign went to a node that holds no
  // keys — an app bug, not the user's problem, so it gets a message that says
  // what to do rather than a JSON-RPC dump.
  { match: 'unknown account', key: 'signerNotWallet' },
  { match: 'user rejected', key: 'userRejected' },
  { match: 'User denied', key: 'userRejected' },
  { match: 'rejected the request', key: 'userRejected' }
]

/**
 * Inspects an ethers error and returns a friendly translated message.
 * Falls back to the raw error text if nothing matches.
 */
export function decodeMarketplaceError(err: unknown, t: TFunction): string {
  const raw = errorText(err)

  for (const entry of ENTRIES) {
    if (raw.includes(entry.match)) {
      // A wallet rejection is a decision, not a fault; recording it would
      // drown the signal it is meant to protect.
      if (entry.key !== 'userRejected') {
        reportClientError({
          message: `${entry.key}: ${raw.slice(0, 300)}`,
          kind: 'transaction',
          handled: true
        })
      }
      return t(`marketplace.errors.${entry.key}`)
    }
  }
  // Nothing matched. The user sees raw provider text in a toast and cannot
  // realistically copy it, so this is exactly the case observability has to
  // capture — flagged unhandled, because a message we cannot name is a gap.
  reportClientError({
    message: raw.slice(0, 400),
    kind: 'transaction',
    handled: false
  })
  return t('marketplace.errors.unknown', { reason: raw })
}
